// Photo profile definitions (§7 of CLAUDE.md).
//
// Each profile drives:
//   - The framing overlay shown on the camera screen
//   - The filename's <profile> segment
//   - Whether a scale reference is required (hands / feet / rash close-ups: yes)
//   - Whether a "subject" suffix is required at capture time (rash → site name)
//
// `cadence`:
//   - 'daily'    appears in the main daily-set list on home
//   - 'asNeeded' shown in a separate section; multiple-per-day allowed for rash
//   - 'weekly'   shown separately, expected ~once per week
//
// `frame` is a normalised rectangle (0–1) inside the viewfinder — purely a
// guide for repeatable framing across days.

// `expectedFrameMm.w` — rough real-world width of the subject when it fills the
// framing box for this profile (toddler-sized estimates). Drives the on-screen
// scale-reference silhouette sizing. Not precision; just a visual guide.

export const PROFILES = {
  // ---- Daily set (in display order, §7) ----
  'hand-L-back':  { id: 'hand-L-back',  label: 'Left hand — back',  cadence: 'daily', requiresScale: true,
                    instruction: 'Back of left hand, fingers spread. Scale reference flat in the same plane.',
                    frame: { x: 0.15, y: 0.18, w: 0.70, h: 0.64 },
                    expectedFrameMm: { w: 70 } },
  'hand-R-back':  { id: 'hand-R-back',  label: 'Right hand — back', cadence: 'daily', requiresScale: true,
                    instruction: 'Back of right hand, fingers spread. Scale reference flat in the same plane.',
                    frame: { x: 0.15, y: 0.18, w: 0.70, h: 0.64 },
                    expectedFrameMm: { w: 70 } },
  'hand-L-palm':  { id: 'hand-L-palm',  label: 'Left hand — palm',  cadence: 'daily', requiresScale: true,
                    instruction: 'Palm of left hand, fingers spread. Scale reference in frame.',
                    frame: { x: 0.15, y: 0.18, w: 0.70, h: 0.64 },
                    expectedFrameMm: { w: 70 } },
  'hand-R-palm':  { id: 'hand-R-palm',  label: 'Right hand — palm', cadence: 'daily', requiresScale: true,
                    instruction: 'Palm of right hand, fingers spread. Scale reference in frame.',
                    frame: { x: 0.15, y: 0.18, w: 0.70, h: 0.64 },
                    expectedFrameMm: { w: 70 } },
  'hand-L-side':  { id: 'hand-L-side',  label: 'Left hand — side',  cadence: 'daily', requiresScale: true,
                    instruction: 'Side profile of left hand (knuckle contour). Good for boggy swelling.',
                    frame: { x: 0.10, y: 0.30, w: 0.80, h: 0.40 },
                    expectedFrameMm: { w: 100 } },
  'hand-R-side':  { id: 'hand-R-side',  label: 'Right hand — side', cadence: 'daily', requiresScale: true,
                    instruction: 'Side profile of right hand (knuckle contour). Good for boggy swelling.',
                    frame: { x: 0.10, y: 0.30, w: 0.80, h: 0.40 },
                    expectedFrameMm: { w: 100 } },
  'foot-L-top':   { id: 'foot-L-top',   label: 'Left foot — top',   cadence: 'daily', requiresScale: true,
                    instruction: 'Top of left foot, toes up. Scale reference flat next to the foot.',
                    frame: { x: 0.20, y: 0.10, w: 0.60, h: 0.78 },
                    expectedFrameMm: { w: 65 } },
  'foot-R-top':   { id: 'foot-R-top',   label: 'Right foot — top',  cadence: 'daily', requiresScale: true,
                    instruction: 'Top of right foot, toes up. Scale reference flat next to the foot.',
                    frame: { x: 0.20, y: 0.10, w: 0.60, h: 0.78 },
                    expectedFrameMm: { w: 65 } },
  'face-front':   { id: 'face-front',   label: 'Face — front',      cadence: 'daily', requiresScale: false,
                    instruction: 'Face square-on, neutral expression. Good light on the face.',
                    frame: { x: 0.20, y: 0.10, w: 0.60, h: 0.80 } },

  // ---- As-needed ----
  'eye-closeup':  { id: 'eye-closeup',  label: 'Eye close-up',      cadence: 'asNeeded', requiresScale: false,
                    instruction: 'Close-up of the affected eye. Daylight if possible.',
                    frame: { x: 0.20, y: 0.30, w: 0.60, h: 0.40 } },
  'rash':         { id: 'rash',         label: 'Rash close-up',     cadence: 'asNeeded', requiresScale: true,
                    requiresSubject: true, multiplePerDay: true,
                    instruction: 'Close-up of the rash. Scale reference flat next to it, same plane.',
                    frame: { x: 0.20, y: 0.20, w: 0.60, h: 0.60 },
                    expectedFrameMm: { w: 60 } },

  // ---- Weekly ----
  'body-front':   { id: 'body-front',   label: 'Body — front',      cadence: 'weekly', requiresScale: false,
                    instruction: 'Full body front. Standing relaxed.',
                    frame: { x: 0.20, y: 0.05, w: 0.60, h: 0.90 } },
  'body-back':    { id: 'body-back',    label: 'Body — back',       cadence: 'weekly', requiresScale: false,
                    instruction: 'Full body back. Standing relaxed.',
                    frame: { x: 0.20, y: 0.05, w: 0.60, h: 0.90 } },
};

/** Common rash sites for the subject picker. "Other" reveals a text input. */
export const RASH_SITES = [
  { id: 'elbow',         label: 'Elbow' },
  { id: 'knee',          label: 'Knee' },
  { id: 'face',          label: 'Face / cheek' },
  { id: 'neck',          label: 'Neck' },
  { id: 'chest',         label: 'Chest / torso' },
  { id: 'back',          label: 'Back' },
  { id: 'ankle',         label: 'Ankle' },
  { id: 'hand',          label: 'Hand / wrist' },
  { id: 'foot',          label: 'Foot' },
  { id: 'behind-ear',    label: 'Behind ear' },
];

/** Sanitise a free-text site into a filename-safe segment. */
export function sanitiseSubject(input) {
  return String(input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
}

/** Compose the effective profile id used in filenames: e.g. 'rash-elbow'. */
export function composeProfileId(baseId, subject) {
  if (!subject) return baseId;
  return `${baseId}-${sanitiseSubject(subject)}`;
}

export function getProfile(id) {
  // Look up by exact id first (normal profiles).
  if (PROFILES[id]) return PROFILES[id];
  // Subject-bearing profiles: e.g. 'rash-elbow' resolves to the 'rash' template.
  const base = id.split('-')[0];
  const tmpl = PROFILES[base];
  if (tmpl?.requiresSubject) return { ...tmpl, id, baseId: base, subject: id.slice(base.length + 1) };
  return null;
}

export function dailyProfiles()    { return Object.values(PROFILES).filter((p) => p.cadence === 'daily'); }
export function asNeededProfiles() { return Object.values(PROFILES).filter((p) => p.cadence === 'asNeeded'); }
export function weeklyProfiles()   { return Object.values(PROFILES).filter((p) => p.cadence === 'weekly'); }
